import React, { createContext, useCallback, useContext, useState } from "react";

import { useAppState } from "../../../../../context/StateContext";
import { Product } from "../../../../../models/product";
import { ProductChangeKeys, ProductChangeOptions, ProductObjects } from "../Maintenance/types";
import { ProductDetailsFields } from "../Maintenance/util";

interface ProductContextProps {
  product?: Product;
  hasUnsavedChanges: boolean;
  changedOptions: ProductChangeKeys;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  handleChangeOptions: (newOption: ProductChangeOptions, noChanges?: boolean) => void;
  setProduct: React.Dispatch<React.SetStateAction<Product>>;
  handleUpdateProduct: (attribute: keyof Product | "all", value: any) => void;
}

const ProductContext = createContext<ProductContextProps>({} as ProductContextProps);

interface ProductProviderProps {
  children: React.ReactChild;
}

const ProductProvider = ({ children }: ProductProviderProps) => {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useAppState();

  const [product, setProduct] = useState<Product>({} as Product);
  const [changedOptions, setChangedOptions] = useState<ProductChangeKeys>({
    [ProductObjects.DETAILS]: false,
  });

  const handleChangeOptions = useCallback(
    (newOption: ProductChangeOptions, noChanges?: boolean) => {
      if (!noChanges) setHasUnsavedChanges(true);

      if (!changedOptions[newOption])
        setChangedOptions((prev) => {
          return {
            ...prev,
            [newOption]: true,
          };
        });
    },
    [changedOptions]
  );

  const handleUpdateProduct = useCallback((attribute: keyof Product | "all", value: any) => {
    const productsDetails = Object.values(ProductDetailsFields) as string[];

    if (productsDetails.includes(attribute)) handleChangeOptions(ProductObjects.DETAILS);

    if (attribute && attribute !== "all") {
      if (!["string", "number", "boolean", "object", "undefined"].includes(typeof value))
        setProduct((state: Product) => ({ ...state, [attribute]: [...value] }));
      else setProduct((state: Product) => ({ ...state, [attribute]: value }));
    } else {
      setProduct({ ...value });
    }
  }, []);

  return (
    <ProductContext.Provider
      value={{
        changedOptions,
        product,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setProduct,
        handleChangeOptions,
        handleUpdateProduct,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

const useProductContext = () => {
  const context = useContext(ProductContext);

  return context;
};

export { ProductContext, ProductProvider, useProductContext };
